import { useQuery } from 'react-query';
import useOrgId from './useOrgId.js';
import { FACEBOOK, INSTAGRAM_SCOPES } from 'constants/socialMedia.js';
import agent from 'superagent';
import config from '../client-config.js';
import useIdToken from './useIdToken.js';

const REFRESH_ALERT_THRESHOLD = 60 * 60 * 24 * 7; // 7 days

const region = encodeURIComponent(config.region || 'us-central1');
const projectId = encodeURIComponent(config.projectId);
const baseUrl = config.socialMediaBaseUrl || `https://${region}-${projectId}.cloudfunctions.net/socialMedia`;

const useMetaAccessTokens = () => {
  const orgId = useOrgId();
  const idToken = useIdToken();

  const { isLoading, isError, data } = useQuery({
    queryKey: ['metaAccessTokens', idToken],
    queryFn: async () => {
      if (!idToken) return null;
      const { body } = await agent.get(`${baseUrl}/${orgId}/facebook/checkPageTokens`)
        .set('Content-Type', 'application/json')
        .set('Authorization', `Bearer ${idToken}`);
      return body;
    },
  });

  // Find the earliest expiration date of all access tokens
  const minExpiresAt = (() => {
    if (!Array.isArray(data)) return Infinity;
    return data.reduce((minExpiresAt, entry) => {
      const { tokenInfo } = entry || {};
      if (tokenInfo === false) return 0; // If the token is invalid, return 0 to force a refresh.
      if (tokenInfo?.data_access_expires_at && tokenInfo.data_access_expires_at < minExpiresAt) {
        return tokenInfo.data_access_expires_at;
      }
      return minExpiresAt;
    }, Infinity);
  })();

  // Determine if the access tokens need to be refreshed
  const needsRefresh = (() => {
    if (!data?.length) return false;

    // If at least one of the access tokens is about to expire (or already has), return true
    const expirationThreshold = Date.now() / 1000 + REFRESH_ALERT_THRESHOLD;
    const hasNearlyExpired = data.some((entry) => {
      const { tokenInfo } = entry || {};
      return tokenInfo?.data_access_expires_at && tokenInfo.data_access_expires_at < expirationThreshold;
    });
    if (hasNearlyExpired) return true;

    // If at least one of the tokens is missing a permission, return true
    const hasAllPermissions = data.every((entry) => {
      if (entry.platform === 'facebook') {
        return FACEBOOK.SOCIAL_SELLING_PERMISSIONS.every((scope) => entry?.tokenInfo?.scopes?.includes(scope));
      }
      if (entry.platform === 'instagram') {
        return INSTAGRAM_SCOPES.every((scope) => entry?.tokenInfo?.scopes?.includes(scope));
      }
      return true;
    });
    return !hasAllPermissions;
  })();

  return {
    isLoading,
    isError,
    data,
    expiresAt: minExpiresAt,
    needsRefresh,
  };
};

export default useMetaAccessTokens;
